import { useMemo } from 'react';
import { addDays, isFuture } from 'date-fns';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import type { ProjectType } from '@atlassian/jira-common-constants/src/project-types.tsx';
import { useProductEntitlementDetails } from '@atlassian/jira-tenant-context-controller/src/components/product-entitlement-details/index.tsx';

import type { Product } from '@atlassian/auto-upgrade-not-a-trial';
import { useProductAndEdition } from '../use-product-and-edition/index.tsx';
import { STANDARD_TRIAL_DAYS_TO_DEACTIVATION } from '../../constants.tsx';

export type AutoUpgradeEligibility = {
	isEligible: boolean;
	deactivationDate?: Date;
	product?: Product;
	projectType?: ProjectType;
};

export const useEligibilityCriteria = (): AutoUpgradeEligibility => {
	const { product, productKey, projectType, isStandardEdition } = useProductAndEdition();

	const isSiteAdmin = useIsSiteAdmin();
	const productEntitlementDetails = useProductEntitlementDetails();
	const entitlementDetails =
		productKey && productEntitlementDetails && productEntitlementDetails[productKey];

	const billingSourceSystem = entitlementDetails?.billingSourceSystem || 'HAMS';
	const trialEndTime = entitlementDetails?.trialEndTime;
	const firstPredunningEndTime = entitlementDetails?.firstPredunningEndTime;

	const deactivationDate = useMemo(() => {
		if (!trialEndTime && !firstPredunningEndTime) return;

		// use firstPredunningEndTime if it's in the future since this will not be removed once the trial ends
		if (firstPredunningEndTime && isFuture(firstPredunningEndTime)) {
			return new Date(firstPredunningEndTime);
		}

		// use trialEndTime if it exists as a backup
		if (trialEndTime) {
			return addDays(new Date(trialEndTime), STANDARD_TRIAL_DAYS_TO_DEACTIVATION);
		}
	}, [trialEndTime, firstPredunningEndTime]);

	const isInTrialOrPreDunning =
		entitlementDetails && !!deactivationDate && isFuture(deactivationDate);

	const isEligible = Boolean(
		isStandardEdition &&
			product &&
			isSiteAdmin &&
			billingSourceSystem === 'CCP' &&
			isInTrialOrPreDunning,
	);

	return useMemo(
		() => ({
			isEligible,
			deactivationDate,
			product,
			projectType,
		}),
		[isEligible, deactivationDate, product, projectType],
	);
};
